import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiFetch } from "../lib/api";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", confirm_password: "", company_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pwReqs = useMemo(() => ({
    length:  form.password.length >= 8,
    upper:   form.password !== form.password.toLowerCase(),
    number:  /\d/.test(form.password),
    special: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(form.password),
    match:   form.password.length > 0 && form.password === form.confirm_password,
  }), [form.password, form.confirm_password]);

  const pwTouched = form.password.length > 0;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (form.full_name.trim().length < 2) { setError("Please enter your full name."); return; }
    if (form.company_name.trim().length < 2) { setError("Please enter your company name."); return; }
    if (!Object.values(pwReqs).every(Boolean)) { setError("Please fix the password requirements below."); return; }

    setLoading(true);
    try {
      const res = await apiFetch("/api/auth/register", {
        method: "POST",
        body: { full_name: form.full_name, email: form.email, password: form.password, company_name: form.company_name },
      });

      if (res.user_id) {
        sessionStorage.setItem("pending_verify_email", form.email);
        sessionStorage.setItem("pending_verify_user_id", res.user_id);
        navigate("/verify-email", { state: { email: form.email, user_id: res.user_id } });
        return;
      }
      setError(res.message || "Registration failed. Please try again.");
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src="https://www.masterfileai.com/hubfs/logo3.png" alt="MasterFileAI" style={{ height: 36, maxWidth: 200, objectFit: 'contain' }}
            onError={e => { e.target.style.display='none'; e.target.nextElementSibling.style.display='block'; }} />
          <span style={{ display:'none', color:'#0f172a', fontWeight:700, fontSize:20 }}>MasterFileAI</span>
        </div>
        <h1 style={styles.heading}>Create your account</h1>
        <p style={styles.sub}>Start cleaning your vendor & customer data today.</p>
        {error && <div style={styles.errorBox}>{error}</div>}
        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Full name</label>
          <input style={styles.input} type="text" placeholder="Jane Smith" value={form.full_name} onChange={set("full_name")} required autoFocus />
          <label style={styles.label}>Company name</label>
          <input style={styles.input} type="text" placeholder="Acme Corp" value={form.company_name} onChange={set("company_name")} required />
          <label style={styles.label}>Work email</label>
          <input style={styles.input} type="email" placeholder="jane@acme.com" value={form.email} onChange={set("email")} required />
          <label style={styles.label}>Password</label>
          <input style={styles.input} type="password" placeholder="Create a password" value={form.password} onChange={set("password")} required />
          {pwTouched && (
            <div style={styles.reqList}>
              <Req ok={pwReqs.length}>At least 8 characters</Req>
              <Req ok={pwReqs.upper}>One uppercase letter</Req>
              <Req ok={pwReqs.number}>One number</Req>
              <Req ok={pwReqs.special}>One special character (!@#$%…)</Req>
            </div>
          )}
          <label style={styles.label}>Confirm password</label>
          <input style={styles.input} type="password" placeholder="Repeat your password" value={form.confirm_password} onChange={set("confirm_password")} required />
          {pwTouched && form.confirm_password.length > 0 && (
            <Req ok={pwReqs.match}>Passwords match</Req>
          )}
          <div style={styles.termsRow}>
            <input type="checkbox" id="terms" required style={{ marginRight: 8, marginTop: 2, flexShrink: 0 }} />
            <label htmlFor="terms" style={styles.termsLabel}>
              I agree to the <a href="/terms" style={styles.link}>Terms of Service</a> and <a href="/privacy" style={styles.link}>Privacy Policy</a>
            </label>
          </div>
          <button type="submit" style={styles.btn} disabled={loading}>
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>
        <p style={styles.footerText}>
          Already have an account?{" "}<Link to="/login" style={styles.link}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function Req({ ok, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: ok ? '#059669' : '#9ca3af', marginTop: 2 }}>
      <span style={{ fontSize: 14 }}>{ok ? '✓' : '○'}</span>
      {children}
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#f0f5fb", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "'Inter', system-ui, sans-serif" },
  card: { background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 16, padding: "48px 40px", width: "100%", maxWidth: 440, boxShadow: "0 4px 24px rgba(1,118,206,0.08)" },
  logoRow: { display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 32 },
  heading: { color: "#0f172a", fontSize: 22, fontWeight: 700, margin: "0 0 8px", letterSpacing: "-0.3px" },
  sub: { color: "#64748b", fontSize: 14, margin: "0 0 28px" },
  errorBox: { background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 8, color: "#dc2626", fontSize: 13, padding: "10px 14px", marginBottom: 20 },
  form: { display: "flex", flexDirection: "column", gap: 6 },
  label: { color: "#64748b", fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 10 },
  input: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, color: "#0f172a", fontSize: 14, padding: "11px 14px", outline: "none", width: "100%", boxSizing: "border-box" },
  termsRow: { display: "flex", alignItems: "flex-start", marginTop: 14, marginBottom: 4 },
  termsLabel: { color: "#64748b", fontSize: 13, lineHeight: 1.5 },
  btn: { marginTop: 20, background: "#F37428", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 15, fontWeight: 600, padding: "13px", width: "100%" },
  reqList: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6, paddingLeft: 2 },
  footerText: { color: "#94a3b8", fontSize: 13, textAlign: "center", marginTop: 24 },
  link: { color: "#0176CE", textDecoration: "none", fontWeight: 500 },
};
